import { useSelector } from "react-redux";
import { useEffect, useState, useRef } from "react";
import { useDispatch } from "react-redux";
import { updateConfig as globalUpdateConfig } from "../../../../store/slices/Global";
import { updateConfig } from "../../../../store/slices/Printing/printingSlice";
import {
  useCreateReissuePrintMutation,
  usePreviewReissuePrintMutation,
  useGetPrintingReissueTableDataQuery,
  useSubmitReissuePrintingListMutation,
} from "../../../../store/queries/Printing";
import { toast } from "react-toastify";
import { getFormData } from "../api";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import Style from "../printing.module.scss";
import QRCode from "react-qr-code";
import html2canvas from "html2canvas";
import ReactDOM from "react-dom";
import { getPreviousPrintRecords } from "./api";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import domtoimage from "dom-to-image-more";
import moment from "moment/moment";
import html2pdf from "html2pdf.js";

const PREVIOUS_PRINT_RENDER_DELAY = 1200;
const IMAGE_EXPORT_SCALE = 4;

const sanitizeFileName = (value, fallback = "certificate") => {
  const cleanedValue = (value || fallback).toString().trim();
  return cleanedValue.replace(/[\\/:*?"<>|]+/g, "_").replace(/\s+/g, "_");
};

const waitForRender = (delay = PREVIOUS_PRINT_RENDER_DELAY) =>
  new Promise((resolve) => {
    window.setTimeout(resolve, delay);
  });

// html2canvas-based capture — matches the PDF output exactly because html2pdf
// also uses html2canvas internally. Use this for card types where borders and
// pixel-exact layout matter (e.g. foldable card sections).
const captureNodeAsJpegHtml2Canvas = async (node) => {
  const canvas = await html2canvas(node, {
    useCORS: true,
    allowTaint: true,
    scale: IMAGE_EXPORT_SCALE,
    scrollY: 0,
    logging: false,
  });
  return canvas.toDataURL("image/jpeg", 1);
};

const captureNodeAsJpeg = (node) =>
  domtoimage.toJpeg(node, {
    cacheBust: true,
    quality: 1,
    width: node.clientWidth * IMAGE_EXPORT_SCALE,
    height: node.clientHeight * IMAGE_EXPORT_SCALE,
    style: {
      transform: `scale(${IMAGE_EXPORT_SCALE})`,
      transformOrigin: "top left",
      width: `${node.clientWidth}px`,
      height: `${node.clientHeight}px`,
    },
    useCORS: true,
  });

const extractSummaryNoFromElement = (node, fallback = "certificate") => {
  const summaryNoElement = Array.from(node?.querySelectorAll("p") || []).find(
    (paragraph) => paragraph.textContent.trim() === "SUMMARY NO",
  );

  const summaryNoValue =
    summaryNoElement &&
    summaryNoElement.parentElement
      ?.querySelectorAll("p")?.[2]
      ?.textContent?.trim();

  return sanitizeFileName(summaryNoValue, fallback);
};

const downloadZipFile = async (zip, fileName) => {
  const content = await zip.generateAsync({ type: "blob" });
  saveAs(content, fileName);
};

const usePrintingTable = ({
  visitingOneFrontPrintRef,
  visitingOneBackPrintRef,
  papperFrontPrintRef,
  papperBackPrintRef,
  bigFrontPrintRef,
  foldableCardRef,
  vistingDuplexRef,
  papperDuplexRef,
}) => {
  let paginationOptions = [
    { label: "40 Per page", value: 40 },
    { label: "60 Per page", value: 60 },
    { label: "80 Per page", value: 80 },
    { label: "100 Per page", value: 100 },
  ];
  const [previousPrintLoading, setPreviousPrintLoading] = useState(false);
  const dispatch = useDispatch();
  const state = useSelector((state) => state.printing);
  const { tableFields } = useSelector((state) => state.global);
  const reportRef = useRef(null);

  const slug = sessionStorage?.getItem("slug");
  const [submitReissuePrintingList] = useSubmitReissuePrintingListMutation();
  const [createReissuePrint] = useCreateReissuePrintMutation();
  const [previewReissuePrint] = usePreviewReissuePrintMutation();
  const [multiPrintLoading, setMultiPrintLoading] = useState(false);

  // Refs/state to drive the "preview before print" flow for multi GLI print.
  const pdfObjRef = useRef(null);
  const filenameRef = useRef("");
  const submittedDataRef = useRef(null);
  const [previewState, setPreviewState] = useState({
    show: false,
    blobUrl: "",
    title: "",
  });

  const showPdfPreview = (pdfObj, title, filename) => {
    pdfObjRef.current = pdfObj;
    filenameRef.current = filename || "";
    const blobUrl = pdfObj.output("bloburl");
    setPreviewState({ show: true, blobUrl, title });
    setMultiPrintLoading(false);
  };

  const handleConfirmPrint = () => {
    // Only finalize the certificate creation on the server once the user
    // confirms via "Print & Upload".
    if (submittedDataRef.current) {
      createReissuePrint(submittedDataRef.current);
    }
    const pdfObj = pdfObjRef.current;
    if (pdfObj) {
      pdfObj.autoPrint();
      window.open(pdfObj.output("bloburl"), "F");
      if (filenameRef.current) {
        pdfObj.save(filenameRef.current);
      }
    }
    if (previewState.blobUrl) {
      try {
        URL.revokeObjectURL(previewState.blobUrl);
      } catch (e) {}
    }
    setPreviewState({ show: false, blobUrl: "", title: "" });
    pdfObjRef.current = null;
    filenameRef.current = "";
    submittedDataRef.current = null;
    resetPrintingDataLater();
  };

  const handleCancelPreview = () => {
    if (previewState.blobUrl) {
      try {
        URL.revokeObjectURL(previewState.blobUrl);
      } catch (e) {}
    }
    setPreviewState({ show: false, blobUrl: "", title: "" });
    pdfObjRef.current = null;
    filenameRef.current = "";
    submittedDataRef.current = null;
    setMultiPrintLoading(false);
    resetPrintingDataLater();
  };
  const {
    data: tableData,
    isFetching,
    isLoading,
  } = useGetPrintingReissueTableDataQuery(
    {
      receipt_id: state?.selectedId,
      search: state.search,
      page_size: state.currentReissuePageSize,
      page: state.currentReissuePage,
      process_slug: slug,
    },
    { skip: state?.is_multi_print },
  );

  const buildReissueListBody = (overrides = {}) => {
    const transformed = state?.transformedGliDataOrder;
    const gliData = Array.isArray(transformed)
      ? transformed.map((entry) => ({
          receipt_id: entry.receipt_id,
          status: entry.status,
        }))
      : [];

    return {
      gli_data: gliData,
      search: state.search ?? "",
      page: state?.currentReissuePage ?? 1,
      per_page: state?.currentReissuePageSize ?? 60,
      work_status: [],
      item_status: null,
      ...overrides,
    };
  };

  const refetchMultiPrintList = (overrides = {}) => {
    const body = buildReissueListBody(overrides);
    submitReissuePrintingList(body)
      .unwrap()
      .then((res) => {
        if (res?.status_code === 200) {
          dispatch(
            updateConfig((s) => {
              const combinedData = [
                ...(s?.multipleTableAllData ?? []),
                ...(res.data?.data ?? []),
              ];
              const deduplicatedData = Array.from(
                new Map(combinedData.map((item) => [item.id, item])).values(),
              );
              s.multipleTableAllData = deduplicatedData;
              s.multipleTableData = res.data;
            }),
          );
        }
      })
      .catch((err) => {
        console.error("Failed to refresh reissue multi GLI list", err);
      });
  };

  const getCurrentTableRows = () => tableData?.data?.data ?? [];

  const resetRenderedPrintingState = () => {
    dispatch(
      updateConfig((currentState) => {
        currentState.printingData = [];
        currentState.printUI = false;
      }),
    );
  };

  const updateRenderedPrintingData = (printingData, cardSize = 1) => {
    dispatch(
      updateConfig((currentState) => {
        currentState.printingData = printingData;
        currentState.printUI = true;
        currentState.card_size = cardSize;
      }),
    );
  };

  const buildZipName = (certificateNumbers, prefix = "previous_print") => {
    const normalizedNumbers = certificateNumbers
      .filter(Boolean)
      .map((item) => sanitizeFileName(item));
    const primaryName = normalizedNumbers[0] || prefix;
    const suffix =
      normalizedNumbers.length > 1
        ? `_plus_${normalizedNumbers.length - 1}`
        : "";

    return `${primaryName}${suffix}_${moment(Date.now()).format(
      "MM_DD_YYYY_HH_mm_ss",
    )}.zip`;
  };

  // Returns the list of capture plans for a given certificate type + side.
  // Each plan describes one ref/selector to capture from. Duplex (side=3)
  // produces two plans (front + back) so each certificate gets two JPGs.
  const getPreviousPrintPlans = (type, side) => {
    if (type === 1) {
      if (side === 2) {
        return [
          // {
          //   suffix: "",
          //   getRoot: () => visitingOneBackPrintRef?.current,
          //   selector: ".item-class-back",
          // },
        ];
      }
      if (side === 3) {
        return [
          {
            suffix: "",
            getRoot: () => visitingOneFrontPrintRef?.current,
            selector: ".item-class",
          },
          // {
          //   suffix: "",
          //   getRoot: () => visitingOneBackPrintRef?.current,
          //   selector: ".item-class-back",
          // },
        ];
      }
      return [
        {
          suffix: "",
          getRoot: () => visitingOneFrontPrintRef?.current,
          selector: ".item-class",
        },
      ];
    }

    if (type === 2) {
      return [
        {
          suffix: "",
          getRoot: () => bigFrontPrintRef?.current,
          selector: ".item-class-big-card",
        },
      ];
    }

    if (type === 3) {
      if (side === 2) {
        return [
          // {
          //   suffix: "",
          //   getRoot: () => papperBackPrintRef?.current,
          //   selector: "#element-to-print",
          // },
        ];
      }
      if (side === 3) {
        return [
          {
            suffix: "",
            getRoot: () => papperFrontPrintRef?.current,
            selector: ".item-class-paper",
          },
          // {
          //   suffix: "",
          //   getRoot: () => papperBackPrintRef?.current,
          //   selector: "#element-to-print",
          // },
        ];
      }
      return [
        {
          suffix: "",
          getRoot: () => papperFrontPrintRef?.current,
          selector: ".item-class-paper",
        },
      ];
    }

    if (type === 4) {
      return [
        // {
        //   suffix: "",
        //   getRoot: () => foldableCardRef?.current,
        //   selector: ".foldable-section-front",
        //   captureMethod: "html2canvas",
        // },
        {
          suffix: "",
          getRoot: () => foldableCardRef?.current,
          selector: ".foldable-section-back",
          captureMethod: "html2canvas",
        },
      ];
    }

    return [];
  };

  const normalizePreviousPrintRecords = (records = []) =>
    records
      .filter((record) => record?.response_data && record?.certificate_type)
      .map((record, index) => ({
        ...record.response_data,
        previous_print_type: record.certificate_type,
        previous_print_card_size: record.card_size ?? 1,
        previous_print_side: record.side ?? 1,
        printed_by: record.printed_by,
        previous_print_index: index,
      }));

  useEffect(() => {
    dispatch(
      globalUpdateConfig((state) => {
        state.tableFields = tableData?.data?.fields;
      }),
    );
    // eslint-disable-next-line
  }, [tableData]);

  const handleCopyText = async (data) => {
    try {
      await navigator.clipboard.writeText(data);
      toast.success("Copied");
    } catch (error) {
      toast.error("Failed to copy");
    }
  };

  const getRow = (feild, data) => {
    const rows = {
      common: (feild, data) => <p className="pro-mb-0">{data[feild]}</p>,
      certificate_number: (feild, data) => (
        <p className="pro-mb-0 pro-d-flex pro-justify-between pro-items-center pro-gap-4">
          <span>{data[feild]}</span>
          <OverlayTrigger
            placement="auto"
            onToggle={(toggle) => {
              const body = document.body;
              body.style.overflow = toggle ? "hidden" : null;
            }}
            overlay={
              <Tooltip id="button-tooltip" className={Style.tooltip}>
                <table className="">
                  <tbody>
                    <tr>
                      <td>Summary No.</td>
                      <td> : </td>
                      <td>{data[feild]}</td>
                    </tr>
                    {data["graded_values"]?.qc_data?.map(
                      (item, index) =>
                        !!item.name && (
                          <tr key={index}>
                            <td>{item?.name}</td>
                            <td> : </td>
                            <td>{item?.value || "-"}</td>
                          </tr>
                        ),
                    )}
                  </tbody>
                </table>
              </Tooltip>
            }
          >
            <span className={Style.info_btn}>
              <span className="material-symbols-outlined pro-fw-medium">
                {" "}
                info{" "}
              </span>
            </span>
          </OverlayTrigger>
        </p>
      ),
      status: (_, data) =>
        data?.status === "Rejected" ? (
          <span className={"pro-badge badge-danger-outline pro-d-inline-block"}>
            {"Rejected"}
          </span>
        ) : data?.status === "Marked" ? (
          <span className={"outline-primary pro-badge pro-d-inline-block"}>
            {"Marked"}
          </span>
        ) : data?.status === "Completed" ? (
          <span className={"outline-success pro-badge pro-d-inline-block"}>
            {"Completed"}
          </span>
        ) : data?.status === "Pending" ? (
          <span className={"outline-warning pro-badge pro-d-inline-block"}>
            {"Pending"}
          </span>
        ) : (
          <span className={"outline pro-badge pro-d-inline-block"}>
            {data?.status}
          </span>
        ),
      grading_status: (_, data) =>
        data?.grading_status === "Rejected" ? (
          <span className={"pro-badge badge-danger-outline pro-d-inline-block"}>
            {"Rejected"}
          </span>
        ) : data?.grading_status === "Marked" ? (
          <span className={"outline-primary pro-badge pro-d-inline-block"}>
            {"Marked"}
          </span>
        ) : data?.grading_status === "Completed" ? (
          <span className={"outline-success pro-badge pro-d-inline-block"}>
            {"Completed"}
          </span>
        ) : data?.grading_status === "Pending" ? (
          <span className={"outline-warning pro-badge pro-d-inline-block"}>
            {"Pending"}
          </span>
        ) : (
          <span className={"outline pro-badge pro-d-inline-block"}>
            {data?.grading_status}
          </span>
        ),
      url: (field, data) =>
        data?.[field] ? (
          <div className="pro-d-flex pro-items-center pro-gap-2 no-wrap">
            <p className="pro-mb-0">{data[field]}</p>
            <span
              className={Style.copy_text}
              onClick={() => handleCopyText(data[field])}
            >
              <span className="material-symbols-outlined">content_copy</span>
            </span>
            <span
              className={Style.copy_text}
              onClick={() => handleDownload(data)}
            >
              <span className="material-symbols-outlined">download</span>
            </span>
          </div>
        ) : (
          ""
        ),
    };
    return rows?.[feild]?.(feild, data) ?? rows["common"](feild, data);
  };

  const handleDownload = async (data) => {
    try {
      const qrCodeContainer = document.createElement("div");
      qrCodeContainer.style.display = "inline-block";
      document.body.appendChild(qrCodeContainer);

      ReactDOM.render(<QRCode value={data?.url} size={150} />, qrCodeContainer);

      const canvas = await html2canvas(qrCodeContainer, { scale: 2 });
      const qrCodeImage = canvas.toDataURL("image/jpeg");
      const downloadLink = document.createElement("a");
      downloadLink.href = qrCodeImage;

      downloadLink.download = `${data?.certificate_number}.jpeg`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);

      document.body.removeChild(qrCodeContainer);
    } catch (error) {
      console.error("Failed to download QR code:", error);
      toast.error("Failed to download QR code");
    }
  };

  const handlePageSize = ({ value: page_size }) => {
    dispatch(
      updateConfig((state) => {
        state.currentReissuePageSize = page_size;
        state.currentReissuePage = 1;
      }),
    );
    if (state?.is_multi_print) {
      refetchMultiPrintList({ page: 1, per_page: page_size });
    }
  };
  const handlePagination = (page) => {
    dispatch(
      updateConfig((state) => {
        state.currentReissuePage = page;
      }),
    );
    if (state?.is_multi_print) {
      refetchMultiPrintList({ page });
    }
  };

  const handlePrintClick = (items) => {
    if (state?.is_multi_print) {
      handleMultiPrintWithoutModal(items);
      dispatch(
        updateConfig((s) => {
          s.selectedItems = items;
        }),
      );
      return;
    }
    dispatch(
      updateConfig((state) => {
        state.printModal = true;
        state.selectedItems = items;
      }),
    );
  };
  const handleEditClick = (item) => {
    if (item?.length > 1) {
      toast.warning("Select one for editing");
    } else {
      dispatch(
        updateConfig((state) => {
          state.selectedListId = item?.[0];
          state.isEdit = true;
          state.data = [{ name: "Version 1" }];
          state.activeId = 1;
        }),
      );

      let data = {
        item_details_id: item?.[0],
        item_type_id: state?.itemTypeId,
        process_slug: "qc",
      };
      dispatch(async (dispatch) => {
        try {
          const response = await getFormData(data);
          if (response?.data?.status_code === 200) {
            if (response?.data?.data?.updated_values) {
              if (response?.data?.data?.updated_values?.length !== 0) {
                const newData = response?.data?.data?.updated_values?.map(
                  (_, index) => ({
                    name: `Version ${index + 1}`,
                    disabled: true,
                  }),
                );

                dispatch(
                  updateConfig((state) => {
                    state.data = [...newData];
                  }),
                );
              }
            }

            dispatch(
              updateConfig((state) => {
                state.formData = response?.data?.data;
                state.showForm = true;
              }),
            );
          } else {
            toast.error("Failed to fetch form data");
          }
        } catch (error) {
          console.error("Error:", error);
          toast.error("An error occurred while fetching form data");
        }
      });
    }
  };

  const handleBulkPass = () => {
    dispatch(
      updateConfig((state) => (state.bulkPassModal = !state.bulkPassModal)),
    );
  };

  const handleSearch = (e) => {
    const value = e?.target?.value;
    dispatch(updateConfig((state) => (state.search = value)));
    if (state?.is_multi_print) {
      refetchMultiPrintList({ search: value, page: 1 });
    }
  };

  const addNodeImageToZip = async (
    zip,
    node,
    fileName,
    captureFn = captureNodeAsJpeg,
  ) => {
    const imageData = await captureFn(node);

    if (!imageData) {
      return false;
    }

    zip.file(`${sanitizeFileName(fileName)}.JPG`, imageData.split(",")[1], {
      base64: true,
    });

    return true;
  };

  const exportPreviousPrintZip = async (
    records,
    certificateNumbers,
    progressToastId,
  ) => {
    // Group by (type, side, card_size) because each group needs a separate
    // render pass with its own card_size and a side-specific capture plan.
    const groupedRecords = records.reduce((accumulator, record) => {
      const type = record?.previous_print_type;
      const side = record?.previous_print_side ?? 1;
      const cardSize = record?.previous_print_card_size ?? 1;
      const key = `${type}__${side}__${cardSize}`;

      if (!accumulator[key]) {
        accumulator[key] = { type, side, cardSize, records: [] };
      }

      accumulator[key].records.push(record);
      return accumulator;
    }, {});

    // Count total capture tasks for progress tracking
    let totalCaptures = 0;
    for (const group of Object.values(groupedRecords)) {
      const plans = getPreviousPrintPlans(group.type, group.side);
      totalCaptures += group.records.length * plans.length;
    }

    const zip = new JSZip();
    let generatedCount = 0;
    let processedCount = 0;

    const updateProgress = () => {
      const progress = totalCaptures > 0 ? processedCount / totalCaptures : 0;
      toast.update(progressToastId, {
        render: `Processing ${processedCount} of ${totalCaptures} images...`,
        progress,
      });
    };

    try {
      for (const group of Object.values(groupedRecords)) {
        const { type, side, cardSize, records: typeRecords } = group;
        const plans = getPreviousPrintPlans(type, side);

        if (!plans.length) {
          toast.error(`Previous print is not configured for type ${type}.`);
          continue;
        }

        updateRenderedPrintingData(typeRecords, cardSize);
        await waitForRender();

        for (const plan of plans) {
          const rootElement = plan.getRoot();

          if (!rootElement) {
            toast.error(
              `Previous print is not configured for type ${type} / side ${side}.`,
            );
            continue;
          }

          const captureTargets = Array.from(
            rootElement.querySelectorAll(plan.selector),
          );

          if (!captureTargets.length) {
            toast.error(
              `No printable certificate found for type ${type} / side ${side}.`,
            );
            continue;
          }

          for (let index = 0; index < typeRecords.length; index += 1) {
            const record = typeRecords[index];
            const captureTarget = captureTargets[index];

            if (!captureTarget) {
              processedCount += 1;
              updateProgress();
              continue;
            }

            const baseName = sanitizeFileName(
              record?.certificate_number || record?.gli_number,
              `certificate_${type}_${index + 1}`,
            );
            const fileName = `${baseName}${plan.suffix}`;

            const captureFn =
              plan.captureMethod === "html2canvas"
                ? captureNodeAsJpegHtml2Canvas
                : captureNodeAsJpeg;
            const created = await addNodeImageToZip(
              zip,
              captureTarget,
              fileName,
              captureFn,
            );
            if (created) {
              generatedCount += 1;
            }
            processedCount += 1;
            updateProgress();
          }
        }
      }

      if (!generatedCount) {
        toast.update(progressToastId, {
          render: "No previous print certificates were generated.",
          type: "error",
          isLoading: false,
          autoClose: 4000,
          progress: undefined,
        });
        return;
      }

      toast.update(progressToastId, {
        render: "Creating ZIP file...",
        progress: 0.95,
      });

      await downloadZipFile(zip, buildZipName(certificateNumbers));

      toast.update(progressToastId, {
        render: `Download complete! ${generatedCount} image${generatedCount > 1 ? "s" : ""} exported.`,
        type: "success",
        isLoading: false,
        autoClose: 4000,
        progress: undefined,
      });
    } finally {
      resetRenderedPrintingState();
    }
  };

  const handlePreviousPrint = async (selectedIds = []) => {
    if (previousPrintLoading) {
      toast.warn("Previous print export is already in progress.");
      return;
    }

    if (!selectedIds?.length) {
      toast.warn("Select at least one certificate to continue.");
      return;
    }

    const selectedRows = getCurrentTableRows().filter((item) =>
      selectedIds.includes(item._id),
    );
    const certificateNumbers = selectedRows
      .map((item) => item?.certificate_number)
      .filter(Boolean);

    if (!certificateNumbers.length) {
      toast.error("Selected rows do not contain certificate numbers.");
      return;
    }

    setPreviousPrintLoading(true);

    const progressToastId = toast.loading("Fetching print records...", {
      progress: 0,
    });

    try {
      const response = await getPreviousPrintRecords({
        certificate_number: certificateNumbers,
      });
      const responsePayload = response?.data ?? response;
      if (responsePayload?.status_code !== 200) {
        toast.update(progressToastId, {
          render:
            responsePayload?.message ||
            "Failed to fetch previous print records.",
          type: "error",
          isLoading: false,
          autoClose: 4000,
          progress: undefined,
        });
        return;
      }

      const normalizedRecords = normalizePreviousPrintRecords(
        responsePayload?.data ?? [],
      );

      if (!normalizedRecords.length) {
        toast.update(progressToastId, {
          render: "No previous print records found.",
          type: "error",
          isLoading: false,
          autoClose: 4000,
          progress: undefined,
        });
        return;
      }

      toast.update(progressToastId, {
        render: "Preparing images...",
        progress: 0.05,
      });

      await exportPreviousPrintZip(
        normalizedRecords,
        certificateNumbers,
        progressToastId,
      );
    } catch (error) {
      console.error("Previous print export failed", error);
      toast.update(progressToastId, {
        render: "Failed to export previous print certificates.",
        type: "error",
        isLoading: false,
        autoClose: 4000,
        progress: undefined,
      });
      resetRenderedPrintingState();
    } finally {
      setPreviousPrintLoading(false);
    }
  };

  const buildCreateReissuePrintBody = (items) => {
    const sourceRows = state?.is_multi_print
      ? (state?.multipleTableAllData ?? [])
      : (tableData?.data?.data ?? []);

    const filteredData = sourceRows.filter((item) => items?.includes(item.id));

    const gliFormData = state?.gliform?.gli_data ?? [];

    const findMatchedGLIData = (item) =>
      gliFormData.find(
        (gliItem) =>
          (item?.recheck_gli_number != null &&
            gliItem?.gli_number === item.recheck_gli_number) ||
          (item?.gli_number != null && gliItem?.gli_number === item.gli_number),
      );

    return {
      certificate_type: state?.gliform?.certificate_type?._id ?? "",
      certificate_format: state?.gliform?.certificate_format?.id || 1,
      gli_data: filteredData?.map((item) => {
        const matchedGLIData = findMatchedGLIData(item);
        return {
          gli_number: item.gli_number,
          receipt_id: item.receipt_id,
          report_id: matchedGLIData?.report_id ?? "",
          certificate_id: [item.id],
        };
      }),
      remarks: state?.gliform?.remarks ?? "",
    };
  };

  const handleMultiPrintWithoutModal = (items) => {
    setMultiPrintLoading(true);
    const body = buildCreateReissuePrintBody(items);
    // Keep the create payload so "Print & Upload" can finalize after preview.
    submittedDataRef.current = body;

    previewReissuePrint(body)
      .unwrap()
      .then((response) => {
        if (response?.status_code === 200) {
          dispatch(
            updateConfig((s) => {
              s.printingData = response?.data;
              s.printUI = true;
              s.clear = true;
            }),
          );
          const timmerId = setTimeout(() => {
            dispatch(updateConfig((s) => (s.clear = false)));
            clearTimeout(timmerId);
          }, 100);

          if (state?.gliform?.certificate_format?.id === 2) {
            // Image format has no interactive PDF preview, so finalize the
            // certificate creation immediately.
            createReissuePrint(body);
            submittedDataRef.current = null;
            setMultiPrintLoading(false);
            handleMultiImagePrints(state?.gliform);
          } else {
            handleMultiPDFPrints(state?.gliform);
          }
        } else {
          setMultiPrintLoading(false);
          toast.error("No data available.");
          dispatch(updateConfig((s) => (s.clear = true)));
        }
      })
      .catch((err) => {
        console.error("Reissue multi-print preview failed", err);
        setMultiPrintLoading(false);
        toast.error("Something went wrong");
        dispatch(updateConfig((s) => (s.clear = true)));
      });
  };

  const resetPrintingDataLater = () => {
    setTimeout(() => {
      dispatch(
        updateConfig((s) => {
          s.printingData = [];
          s.printUI = false;
        }),
      );
    }, 500);
  };

  const printPdfFromRef = (ref, options, title = "Preview") => {
    const element = ref?.current;
    if (!element) {
      toast.error("No elements in the DOM");
      setMultiPrintLoading(false);
      return;
    }
    html2pdf()
      .set(options)
      .from(element)
      .outputPdf()
      .get("pdf")
      .then((pdfObj) => {
        showPdfPreview(pdfObj, title, options?.filename);
      });
  };

  const handleMultiPDFPrints = (values) => {
    const ts = `${moment(Date.now()).format("DD_MM_YYYY")}_${moment(Date.now()).format("HH_mm_ss")}`;
    const baseHtml2canvas = {
      dpi: 100,
      letterRendering: true,
      useCORS: true,
      logging: true,
      scale: 4,
      scrollY: 0,
    };

    if (values?.certificate_type?._id === 1) {
      if (values?.side?.id === 1) {
        printPdfFromRef(
          visitingOneFrontPrintRef,
          {
            margin: [0, 9, 0, 9],
            filename: `visiting card certificate_${ts}.pdf`,
            image: { type: "jpeg", quality: 1 },
            html2canvas: { ...baseHtml2canvas, width: 720 },
            jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
            pagebreak: false,
            DisablePdfCompression: true,
          },
          "Visiting Card Certificate - Front",
        );
      } else if (values?.side?.id === 2) {
        printPdfFromRef(
          visitingOneBackPrintRef,
          {
            margin: [0, 9, 0, 9],
            filename: `visiting card certificate_${ts}.pdf`,
            image: { type: "jpeg", quality: 1 },
            html2canvas: { ...baseHtml2canvas, width: 720, scale: 7 },
            jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
            pagebreak: false,
            DisablePdfCompression: true,
          },
          "Visiting Card Certificate - Back",
        );
      } else {
        printPdfFromRef(
          vistingDuplexRef,
          {
            margin: [0, 9, 0, 9],
            filename: `visiting card duplex_${ts}.pdf`,
            image: { type: "jpeg", quality: 1 },
            html2canvas: { ...baseHtml2canvas, width: 720 },
            jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
            DisablePdfCompression: true,
          },
          "Visiting Card Duplex",
        );
      }
    } else if (values?.certificate_type?._id === 2) {
      printPdfFromRef(
        bigFrontPrintRef,
        {
          margin: [5, 5, 5, 5],
          filename: `big card certificate front_${ts}.pdf`,
          image: { type: "jpeg", quality: 1 },
          html2canvas: baseHtml2canvas,
          pagebreak: { avoid: "", mode: ["avoid-all", "css", "legacy"] },
          jsPDF: { unit: "mm", format: "a4", orientation: "landscape" },
          DisablePdfCompression: true,
        },
        "Big Card Certificate - Front",
      );
    } else if (values?.certificate_type?._id === 3) {
      if (values?.side?.id === 1) {
        printPdfFromRef(
          papperFrontPrintRef,
          {
            margin: [0, 5, 0, 5],
            filename: `paper certificate front_${ts}.pdf`,
            image: { type: "jpeg", quality: 1 },
            html2canvas: baseHtml2canvas,
            jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
            DisablePdfCompression: true,
          },
          "Paper Certificate - Front",
        );
      } else if (values?.side?.id === 2) {
        printPdfFromRef(
          papperBackPrintRef,
          {
            margin: [5, 5, 5, 5],
            filename: `paper certificate back_${ts}.pdf`,
            image: { type: "jpeg", quality: 1 },
            html2canvas: baseHtml2canvas,
            pagebreak: { avoid: "", mode: ["avoid-all", "css", "legacy"] },
            jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
            DisablePdfCompression: true,
          },
          "Paper Certificate - Back",
        );
      } else {
        printPdfFromRef(
          papperDuplexRef,
          {
            margin: [0, 5, 0, 5],
            filename: `paper certificate duplex_${ts}.pdf`,
            image: { type: "jpeg", quality: 1 },
            html2canvas: baseHtml2canvas,
            jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
            pagebreak: { avoid: "", mode: ["avoid-all", "css", "legacy"] },
            DisablePdfCompression: true,
          },
          "Paper Certificate Duplex",
        );
      }
    } else if (values?.certificate_type?._id === 4) {
      printPdfFromRef(
        foldableCardRef,
        {
          margin: [12, 5, 0, 5],
          filename: `foldable_card_${ts}.pdf`,
          image: { type: "jpeg", quality: 1 },
          html2canvas: baseHtml2canvas,
          pagebreak: { avoid: "", mode: ["avoid-all", "css", "legacy"] },
          jsPDF: { unit: "mm", format: "a5", orientation: "landscape" },
          DisablePdfCompression: true,
        },
        "Foldable Card",
      );
    }
  };

  const captureItemsToZip = async (rootRef, selector, zipPrefix) => {
    const element = rootRef?.current;
    if (!element) {
      toast.error("No elements in the DOM");
      return;
    }
    const items = element.querySelectorAll(selector);
    if (items.length === 0) {
      toast.error(`No elements found with the class '${selector}'.`);
      return;
    }

    const zip = new JSZip();
    let generated = 0;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const fileName = extractSummaryNoFromElement(
        item,
        `certificate_${i + 1}`,
      );
      try {
        const imgData = await captureNodeAsJpeg(item);
        if (imgData) {
          zip.file(`${fileName}.JPG`, imgData.split(",")[1], { base64: true });
          generated += 1;
        }
      } catch (err) {
        console.error("Capture failed", err);
      }
    }

    if (!generated) {
      toast.error("No images generated");
      return;
    }

    const content = await zip.generateAsync({ type: "blob" });
    saveAs(
      content,
      `${zipPrefix}_${moment(Date.now()).format("MM_DD_YYYY_HH_mm_ss")}.zip`,
    );
    resetPrintingDataLater();
  };

  const handleMultiImagePrints = (values) => {
    setTimeout(() => {
      if (values?.certificate_type?._id === 1 && values?.side?.id === 1) {
        captureItemsToZip(
          visitingOneFrontPrintRef,
          ".item-class-recheck",
          "visiting_cards_images",
        );
      } else if (values?.certificate_type?._id === 2) {
        captureItemsToZip(
          bigFrontPrintRef,
          ".item-class-big-card",
          "big_cards_images",
        );
      }
    }, 1000);
  };

  return {
    previousPrintLoading,
    isFetching,
    isLoading,
    tableData,
    tableFields,
    paginationOptions,
    state,
    multiPrintLoading,
    getRow,
    handleBulkPass,
    handlePageSize,
    handlePagination,
    handlePrintClick,
    handleEditClick,
    handleSearch,
    handlePreviousPrint,
    reportRef,
    previewState,
    handleConfirmPrint,
    handleCancelPreview,
  };
};

export default usePrintingTable;
